import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Link, useHistory } from 'react-router-dom';
import { connect, useDispatch, useSelector } from 'react-redux';
import { cartSizeSelector } from 'redux/selectors/cart.selector';
import { getSearch } from 'redux/actions/product.actions';
import { openLoginModal } from 'redux/actions/modal.actions';
import { formatCurrencyARS } from 'helpers/currency';
import './BrandHeader67.scss';

const BrandHeader67 = ({ client, categories = [], cartSize = 0, onOpenCart }) => {
  const [search, setSearch] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [suggestionsOpen, setSuggestionsOpen] = useState(false);
  const history = useHistory();
  const dispatch = useDispatch();
  const searchBoxRef = useRef(null);
  const user = useSelector((state) => state.user?.data);
  const suggestions = useSelector((state) => state.product?.search || []);
  const catalogProducts = useSelector((state) => state.product?.items || []);
  const searchLoading = useSelector((state) => state.product?.searchLoading || false);
  const searchQuery = search.trim();
  const normalizedQuery = searchQuery.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();

  const visibleSuggestions = useMemo(() => {
    if (normalizedQuery.length < 2) return [];
    const matches = (product) => {
      const searchable = [product?.name, product?.description, ...(Array.isArray(product?.tags) ? product.tags.map((tag) => tag?.name) : [])]
        .filter(Boolean)
        .join(' ')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
      return searchable.includes(normalizedQuery);
    };
    const byId = new Map();
    [...(suggestions || []), ...(catalogProducts || [])].forEach((product) => {
      if (product && product.stock > 0 && product.code && matches(product)) byId.set(product.id, product);
    });
    return [...byId.values()].slice(0, 8);
  }, [catalogProducts, normalizedQuery, suggestions]);

  useEffect(() => {
    const normalized = searchQuery.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
    if (normalized.length < 3) {
      dispatch(getSearch(''));
      return undefined;
    }
    const timeout = setTimeout(() => dispatch(getSearch(normalized)), 250);
    return () => clearTimeout(timeout);
  }, [dispatch, searchQuery]);

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (searchBoxRef.current && !searchBoxRef.current.contains(event.target)) setSuggestionsOpen(false);
    };
    document.addEventListener('mousedown', closeOnOutsideClick);
    return () => document.removeEventListener('mousedown', closeOnOutsideClick);
  }, []);

  const openProduct = (product) => {
    if (!product?.code) return;
    setSuggestionsOpen(false);
    history.push(`/producto/${encodeURIComponent(product.code)}`);
  };

  const openAccount = () => {
    if (user) history.push('/perfil');
    else dispatch(openLoginModal());
  };

  const submitSearch = (event) => {
    event.preventDefault();
    const value = normalizedQuery;
    if (value) {
      setSuggestionsOpen(false);
      history.push(`/busqueda/${encodeURIComponent(value)}`);
    }
  };
  const storeName = client?.name_show || client?.name || 'Librería & Regalería';
  const logo = client?.image;

  return (
    <header className="brand67-header">
      <div className="brand67-header__top">Un espacio para descubrir, regalar y disfrutar</div>
      <div className="brand67-header__main">
        <Link to="/home" className="brand67-header__brand" aria-label={`Inicio ${storeName}`}>
          {logo ? <img src={logo} alt={storeName} /> : <span>{storeName}</span>}
        </Link>
        <form className="brand67-header__search" onSubmit={submitSearch} role="search" ref={searchBoxRef}>
          <input
            aria-label="Buscar productos"
            aria-autocomplete="list"
            aria-controls="brand67-search-suggestions"
            placeholder="¿Qué estás buscando?"
            value={search}
            onFocus={() => setSuggestionsOpen(true)}
            onChange={(event) => { setSearch(event.target.value); setSuggestionsOpen(true); }}
            onKeyDown={(event) => {
              if (event.key === 'Escape') setSuggestionsOpen(false);
              if (event.key === 'Enter' && suggestionsOpen && visibleSuggestions.length) {
                event.preventDefault();
                openProduct(visibleSuggestions[0]);
              }
            }}
          />
          <button type="submit" aria-label="Buscar"><i className="fa fa-search" aria-hidden="true" /></button>
          {suggestionsOpen && searchQuery.length >= 3 && (
            <div className="brand67-search-suggestions" id="brand67-search-suggestions" role="listbox" aria-label="Productos sugeridos">
              {(visibleSuggestions.length > 0 || searchLoading) && (
                <div className="brand67-search-suggestions__header">
                  <strong>Productos sugeridos</strong>
                  {!searchLoading && <span>{visibleSuggestions.length} {visibleSuggestions.length === 1 ? 'resultado' : 'resultados'}</span>}
                </div>
              )}
              {visibleSuggestions.map((product) => (
                <button className="brand67-search-suggestions__item" type="button" role="option" aria-selected="false" key={product.id} onMouseDown={(event) => event.preventDefault()} onClick={() => openProduct(product)}>
                  <span className="brand67-search-suggestions__image">
                    {(product.image1 || client?.image) && <img src={product.image1 || client.image} alt="" loading="lazy" />}
                  </span>
                  <span className="brand67-search-suggestions__details">
                    <strong>{product.name}</strong>
                    <span>{formatCurrencyARS(product.sale || product.price)}</span>
                  </span>
                  <i className="fa fa-chevron-right brand67-search-suggestions__arrow" aria-hidden="true" />
                </button>
              ))}
              {searchLoading && visibleSuggestions.length === 0 && <div className="brand67-search-suggestions__message">Buscando productos…</div>}
              {!searchLoading && visibleSuggestions.length === 0 && <div className="brand67-search-suggestions__message">No encontramos productos</div>}
            </div>
          )}
        </form>
        <div className="brand67-header__actions">
          <button className="brand67-header__menu-toggle" type="button" onClick={() => setMenuOpen((open) => !open)} aria-label={menuOpen ? 'Cerrar menú' : 'Abrir menú'} aria-expanded={menuOpen} aria-controls="brand67-main-navigation">
            <i className={`fa ${menuOpen ? 'fa-times' : 'fa-bars'}`} aria-hidden="true" />
            <span>{menuOpen ? 'Cerrar' : 'Menú'}</span>
          </button>
          <button type="button" onClick={openAccount} aria-label={user ? 'Abrir mi cuenta' : 'Iniciar sesión o crear cuenta'}>
            <i className="fa fa-user-o" aria-hidden="true" /><span>{user ? 'Mi cuenta' : 'Ingresar'}</span>
          </button>
          <button type="button" onClick={onOpenCart} aria-label={`Abrir carrito, ${cartSize} productos`}>
            <i className="fa fa-shopping-bag" aria-hidden="true" /><span>Mi carrito</span><b>{cartSize}</b>
          </button>
        </div>
      </div>
      <nav id="brand67-main-navigation" className={`brand67-header__nav${menuOpen ? ' is-open' : ''}`} aria-label="Navegación principal">
        <Link to="/productos" onClick={() => setMenuOpen(false)}>Todos los productos</Link>
        {categories.slice(0, 5).map(({ category }) => category?.code && (
          <Link key={category.id || category.code} to={`/productos/${category.code}`} onClick={() => setMenuOpen(false)}>{category.name}</Link>
        ))}
        <Link to="/ofertas" onClick={() => setMenuOpen(false)}>Ofertas</Link>
      </nav>
    </header>
  );
};

BrandHeader67.propTypes = {
  client: PropTypes.shape(),
  categories: PropTypes.arrayOf(PropTypes.shape()),
  cartSize: PropTypes.number,
  onOpenCart: PropTypes.func.isRequired,
};

export default connect((state) => ({ cartSize: cartSizeSelector(state) }))(BrandHeader67);
